import {Component, computed, input, model} from '@angular/core';
import {InviteDelivery} from './grant.models';

/** One rendered segment: its value, the words on it, and whether it may be chosen at all. */
interface DeliveryOption {
  value: InviteDelivery;
  label: string;
  disabled: boolean;
}

/**
 * How an invitation reaches the person it is for — asked the same way everywhere one is issued (#568).
 *
 * ### Why one component rather than three controls that happen to agree
 *
 * Three screens issue invitations (the employee roster's create form, an employee's „Anmeldung" card, and
 * access management), and before this each of them asked about delivery in its own shape: a select with three
 * options, a switch, and — on the employee card — nothing at all, so the invitation was always link-only and
 * the mail had to be chased afterwards with „Einladung erneut senden". The same decision in three widgets is
 * three places to drift, and the one that was missing is what the feedback behind this change was about.
 *
 * So delivery is now **one control, with the same two words on it everywhere**: „Per E-Mail senden" or
 * „Nur Link erzeugen". The roster adds a third segment, „Keine Einladung", because an employee record for
 * somebody who will never sign in is an ordinary thing to create — see {@link allowNone}.
 *
 * ### Why radios, not a switch and not a select
 *
 * The style guide's „Segmented choice inside a form (one of two, radios)" pattern, for its stated reason: this
 * is a choice between named intentions that belongs to the form's state, so real `<input type="radio">`s bring
 * the grouped announcement, the roving focus and the arrow keys for free, and the wrapping `<label>` names each
 * segment without an `aria-label`.
 *
 * A switch was the wrong shape for a different reason: „Einladung per E-Mail senden" OFF does not say what
 * happens instead. Both outcomes are real outcomes here — a link handed over in person is the *normal* way an
 * employee-link code travels — and a control that names only one of them makes the other one look like a
 * failure state.
 *
 * ### The unavailable case is rendered, never removed
 *
 * Without a usable address there is nothing to send to, and the server refuses the combination
 * (`E_GRANT_INVITE_EMAIL_REQUIRED`). The segment is therefore **disabled and still on screen**, with
 * {@link unavailableReason} naming what is missing: an option that vanishes takes its own explanation with it,
 * and the reason to type an address has to be visible *before* it is typed. (This is what the roster used to
 * get wrong in the other direction — it dropped the option from the list and printed a sentence beside it.)
 *
 * Contrast is load-bearing on the disabled segment: it is carried by a `bg-gray-100` surface and `text-gray-600`
 * words, never by dimming, because an opacity multiplies into the `text-xs` reason below and drops the one
 * sentence explaining the state under the WCAG AA floor. Pinned by the axe specs.
 */
@Component({
  selector: 'company-invite-delivery-choice',
  standalone: true,
  template: `
    <fieldset>
      <legend class="block text-sm font-medium leading-6 text-gray-900">{{ legend() }}</legend>

      <!-- Stacked on a phone, segmented from sm up: „Per E-Mail senden" and „Nur Link erzeugen" do not fit
           side by side in half of a 320px column without wrapping to three lines. -->
      <div class="mt-2 grid grid-cols-1 gap-2"
           [class.sm:grid-cols-2]="!allowNone()" [class.sm:grid-cols-3]="allowNone()">
        @for (option of options(); track option.value) {
          <!-- focus-within:ring is not optional: the input is sr-only, so without it a keyboard user has no
               visible focus at all. Selection is carried by the border AND the surface, never by colour
               alone. -->
          <label class="rounded-xl border-2 px-3 py-2 text-center text-sm font-semibold transition-all
                        duration-200 focus-within:ring-2 focus-within:ring-theme-primary-700"
                 [class.cursor-pointer]="!option.disabled"
                 [class.cursor-not-allowed]="option.disabled"
                 [class.border-theme-primary-700]="value() === option.value && !option.disabled"
                 [class.bg-theme-primary-50]="value() === option.value && !option.disabled"
                 [class.text-theme-primary-900]="value() === option.value && !option.disabled"
                 [class.border-gray-200]="value() !== option.value || option.disabled"
                 [class.bg-white]="value() !== option.value && !option.disabled"
                 [class.bg-gray-100]="option.disabled"
                 [class.text-gray-600]="value() !== option.value || option.disabled">
            <input type="radio" class="sr-only" [name]="name()" [value]="option.value"
                   [id]="name() + '-' + option.value"
                   [checked]="value() === option.value" [disabled]="option.disabled"
                   [attr.aria-describedby]="option.disabled ? name() + '-reason' : null"
                   (change)="choose(option.value)"/>
            {{ option.label }}
          </label>
        }
      </div>

      <!-- Why the mail is not on offer, said where the option is — gray-700 rather than a hint grey, because at
           text-xs the lighter shade fails WCAG AA and axe calls it "serious". -->
      @if (!emailAvailable() && unavailableReason()) {
        <p [id]="name() + '-reason'" class="mt-1 text-xs text-gray-700">{{ unavailableReason() }}</p>
      }

      @if (hint()) {
        <p class="mt-1 text-xs text-gray-700">{{ hint() }}</p>
      }
    </fieldset>
  `
})
export class InviteDeliveryChoiceComponent {
  /**
   * The chosen delivery. A `model`, so a caller may either two-way bind it or — where the value is *derived*,
   * as in access management, whose default follows the address field — bind it one-way and answer the output.
   */
  readonly value = model.required<InviteDelivery>();

  /**
   * Whether „Per E-Mail senden" may be chosen: there has to be an address the server would accept.
   *
   * The caller decides what that means for its screen (a typed field, an employee's stored address), and always
   * via `looksLikeEmail` so the three spots agree. False disables the segment rather than removing it.
   */
  readonly emailAvailable = input.required<boolean>();

  /** What is missing, shown only while {@link emailAvailable} is false. Named as the remedy, not as a rule. */
  readonly unavailableReason = input('');

  /** An always-visible sentence under the segments — what the chosen delivery will actually do. */
  readonly hint = input('');

  /**
   * Whether to offer „Keine Einladung" as a third segment.
   *
   * Only the roster's create form does: plenty of employee records exist for people who will never sign in, and
   * minting a one-time code for every create would produce invitations nobody ever redeems. The other two spots
   * are reached by pressing a button that says „Einladung erstellen", where declining is the Cancel key.
   */
  readonly allowNone = input(false);

  /**
   * The radio group's `name`, and the stem of each segment's `id` (`{{name}}-email`, `-link`, `-none`).
   *
   * Required and per-screen, because two radio groups sharing a name in one document are ONE group: a page that
   * ever rendered two of these would silently deselect the other's choice.
   */
  readonly name = input.required<string>();

  /** The question, as the group's `<legend>` — the accessible name every segment is announced under. */
  readonly legend = input('Zustellung der Einladung');

  /**
   * The segments, in the order they are offered. „Per E-Mail senden" leads because it is the default wherever
   * an address is known, and the default belongs where the eye lands first.
   */
  protected readonly options = computed<DeliveryOption[]>(() => {
    const options: DeliveryOption[] = [
      {value: 'email', label: 'Per E-Mail senden', disabled: !this.emailAvailable()},
      {value: 'link', label: 'Nur Link erzeugen', disabled: false}
    ];
    if (this.allowNone()) options.push({value: 'none', label: 'Keine Einladung', disabled: false});
    return options;
  });

  /**
   * Records the choice.
   *
   * The disabled guard is belt and braces — a disabled radio fires no `change` — but it is the difference
   * between a component that cannot emit an impossible value and one that merely is not asked to.
   */
  protected choose(value: InviteDelivery): void {
    if (value === 'email' && !this.emailAvailable()) return;
    this.value.set(value);
  }
}
